import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useMsal } from "@azure/msal-react";
import "./Header.css";
//Custom Components
import NotificationList from "./header-components/NotificationList";
import ProfileControls from "./header-components/ProfileControls";
import { useDataContext } from "../../data-context/DataContextProvider";
import { ENVIRONMENT_LABEL } from "../../utils/environment";
import { useTranslation, TranslationKey } from "@preppa/translation";
import { useAnchoredDropdownPosition, useIsOffline } from "@preppa/shared-ui";
//SKAPA Components
import Button from "@ingka/button";
import Text from "@ingka/text";
//SKAPA Icons
import settingsIcon from "@ingka/ssr-icon/paths/gear";
import notificationIcon from "@ingka/ssr-icon/paths/bell";
import activeNotificationIcon from "@ingka/ssr-icon/paths/bell-active";
//SKAPA Styles
import "@ingka/button/dist/style.css";
import "@ingka/svg-icon/dist/style.css";
import "@ingka/focus/dist/style.css";

const MONTH_KEYS: TranslationKey[] = [
  "months.jan", "months.feb", "months.mar", "months.apr",
  "months.may", "months.jun", "months.jul", "months.aug",
  "months.sep", "months.oct", "months.nov", "months.dec"
];

function Header() {
  const { notifications, hasUnseenNotifications, storeSelectionRequired, dispatch } = useDataContext();
  const { translate } = useTranslation();
  const isOffline = useIsOffline();
  const { accounts } = useMsal();
  const [isStoreButtonSelected, setIsStoreButtonSelected] = useState(false);
  const [isNotificationButtonSelected, setIsNotificationButtonSelected] =
    useState(false);

  const userRoles = (accounts[0]?.idTokenClaims as { roles?: string[] })?.roles || [];
  const isSystemAdmin = userRoles.some((role) => role.toLowerCase() === "system.administrator");

  useEffect(() => {
    if (storeSelectionRequired) {
      setIsStoreButtonSelected(true);
    }
  }, [storeSelectionRequired]);

  const hasNotifications = notifications.length > 0;
  const lastUpdatedLabel = hasNotifications
    ? (() => {
        const date = notifications[0].timestamp;
        const day = date.getDate();
        const month = translate(MONTH_KEYS[date.getMonth()]);
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${translate("header.lastUpdated")} ${day} ${month} ${hours}.${minutes}`;
      })()
    : translate("header.lastUpdated");

  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const storeButtonRef = useRef<HTMLButtonElement>(null);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const storeDropdownRef = useRef<HTMLDivElement>(null);

  const {
    dropdownStyle: storeDropdownStyle,
    updatePosition: updateStoreDropdownPosition,
  } = useAnchoredDropdownPosition(storeButtonRef, isStoreButtonSelected);

  const {
    dropdownStyle: notificationDropdownStyle,
    updatePosition: updateNotificationDropdownPosition,
  } = useAnchoredDropdownPosition(
    notificationButtonRef,
    isNotificationButtonSelected
  );

  // The store dropdown can also open without a click (storeSelectionRequired).
  useLayoutEffect(() => {
    if (isStoreButtonSelected) {
      updateStoreDropdownPosition();
    }
  }, [isStoreButtonSelected, updateStoreDropdownPosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      const clickedInsideNotification =
        notificationButtonRef.current?.contains(target) ||
        notificationDropdownRef.current?.contains(target);

      const clickedInsideStore =
        storeButtonRef.current?.contains(target) ||
        storeDropdownRef.current?.contains(target);

      if (!clickedInsideNotification) {
        setIsNotificationButtonSelected(false);
      }
      if (!clickedInsideStore && !storeSelectionRequired) {
        setIsStoreButtonSelected(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [storeSelectionRequired]);

  function handleStoreChange() {
    setIsStoreButtonSelected(false);
  }

  const appVersion = process.env.REACT_APP_VERSION || '0.0.0';

  return (
    <div className="header-wrapper">
      <div className="banner">
        <div className="title-container">
          <Text className="title-text" tagName="h3">
            {isSystemAdmin ? `PREPPA - ${ENVIRONMENT_LABEL}` : "PREPPA"}
          </Text>
          {/* <span className="version-label">v{appVersion}</span> */}
        </div>
        <div id="banner-controls-container">
          {isOffline && <span className="offline-indicator">{translate("header.noInternetConnection")}</span>}
          <label id="notification-label">{lastUpdatedLabel}</label>
          <div className="notification-button-wrapper">
            <Button
              ref={notificationButtonRef}
              id="notification-button"
              ssrIcon={hasUnseenNotifications ? activeNotificationIcon : notificationIcon}
              type="tertiary"
              iconOnly={true}
              inverseTheme={true}
              size="small"
              onClick={() => {
                updateNotificationDropdownPosition();
                setIsNotificationButtonSelected((prev) => !prev);
                setIsStoreButtonSelected(false);
                dispatch({ type: "MARK_NOTIFICATIONS_SEEN" });
              }}
            />
            {isNotificationButtonSelected && (
              <div
                ref={notificationDropdownRef}
                className="notification-dropdown-wrapper"
                style={notificationDropdownStyle}
              >
                <NotificationList />
              </div>
            )}
          </div>
          <div className="store-button-wrapper">
            <Button
              ref={storeButtonRef}
              id="store-button"
              ssrIcon={settingsIcon}
              type="tertiary"
              iconOnly={true}
              inverseTheme={true}
              size="small"
              onClick={() => {
                if (!storeSelectionRequired) {
                  updateStoreDropdownPosition();
                  setIsStoreButtonSelected((prev) => !prev);
                }
                setIsNotificationButtonSelected(false);
              }}
            />
            {isStoreButtonSelected && (
              <div
                ref={storeDropdownRef}
                className="store-dropdown-wrapper"
                style={storeDropdownStyle}
              >
                <ProfileControls onStoreChange={() => handleStoreChange()} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
